import type { SubscriptionContractDto } from '#shared/types/core/ecommerce/subscriptions'

interface SubscriptionPrice {
  perLicence: number
  total: number
}

export interface SubscriptionPurchaseType {
  type: 'subscription'
  uuid: UUID
  isbn: ISBN
  product?: string
  access: number
  renewal?: string
  initial: SubscriptionPrice
  recurring: SubscriptionPrice
  ownerId: string
  isCancelled: boolean
}

export function useSubscriptionsQueryOptions() {
  const api = useSubscriptionsApi()

  const auth = useAuthorizeStore()

  return queryOptions({
    enabled: computed(() => auth.loggedIn),
    queryKey: [auth.user?.userId, 'subscriptions', auth.user?.selectedOrganization?.number],
    staleTime: 1000 * 60 * 5, // 5 minutes
    async queryFn() {
      const res = await api.getSubscriptions()

      return (res ?? [])
        .filter((it) => it.ownerId === auth.user?.selectedOrganization?.number)
        .sort(
          (a, b) =>
            new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime(),
        )
        .map(toSubscription)
    },
  })
}

export function useSubscriptions() {
  const options = useSubscriptionsQueryOptions()

  return useQuery(options)
}

function toSubscription(
  subscription: SubscriptionContractDto,
): SubscriptionPurchaseType {
  return {
    type: 'subscription',
    ownerId: subscription.ownerId,
    uuid: subscription.uuid,
    isbn: (subscription.cartItem?.productId || subscription.productId) as ISBN,
    product: subscription.cartItem?.productName || subscription.productName,
    initial: subscriptionPrice(subscription, 'initial'),
    recurring: subscriptionPrice(subscription, 'recurring'),
    access: subscription.cartItem?.amount ?? subscription.quantity ?? 1,
    renewal: subscription.renewAt ? formatNorwegianDate(new Date(subscription.renewAt)) : undefined,
    isCancelled: subscription.status === 'CANCELLED',
  }
}

function subscriptionPrice(
  subscription: SubscriptionContractDto,
  key: 'initial' | 'recurring',
): SubscriptionPrice {
  const total = subscription[`${key}Price`] / 100
  return {
    perLicence: total / (subscription.cartItem?.amount ?? subscription.quantity ?? 1),
    total,
  }
}
